import { useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react'
import { usePatientRecord } from '@/hooks/usePatientRecord'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import MedicalHistoryPanel from '@/components/case-review/MedicalHistoryPanel'
import { formatDate } from '@/lib/dates'
import { formatConditionCategory, formatVisitTypeCategory, formatSex, formatGender } from '@/lib/format'
import LoadingPage from '@/components/shared/LoadingPage'
import ErrorState from '@/components/shared/ErrorState'
import type { VisitTimelineEntry } from '@/types/patient'

function calculateAge(dob: string): number {
  const birth = new Date(`${dob}T00:00:00`)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'completed': return 'default'
    case 'documented': return 'secondary'
    case 'scheduled': return 'outline'
    default: return 'secondary'
  }
}

function VisitEntry({ visit }: { visit: VisitTimelineEntry }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-lg border bg-white">
      <button
        type="button"
        className="flex w-full items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
        aria-expanded={expanded}
        aria-label={`${formatVisitTypeCategory(visit.visit_type)} visit on ${formatDate(visit.date)} with ${visit.provider_name}`}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
        )}
        <div className="flex flex-1 items-center justify-between min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <Badge variant="outline">{formatVisitTypeCategory(visit.visit_type)}</Badge>
            <span className="text-sm font-medium text-slate-900">{formatDate(visit.date)}</span>
            <span className="text-sm text-slate-500 truncate">{visit.provider_name}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="secondary">{formatConditionCategory(visit.condition_category)}</Badge>
            <Badge variant={statusVariant(visit.status)}>{visit.status}</Badge>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t px-4 pb-4 pt-3 space-y-3">
          {visit.note_summary && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">Note Summary</h4>
              <p className="text-sm text-slate-700">{visit.note_summary}</p>
            </div>
          )}
          {visit.treatment_plan_summary && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">Treatment Plan</h4>
              <p className="text-sm text-slate-700">{visit.treatment_plan_summary}</p>
            </div>
          )}
          {visit.photos.length > 0 && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-1">Photos</h4>
              <div className="flex gap-2">
                {visit.photos.map((photo) => (
                  <img
                    key={photo.id}
                    src={photo.thumbnail_url}
                    alt={photo.tag}
                    className="h-16 w-16 rounded border object-cover"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function PatientRecordPage() {
  const { patientId } = useParams<{ patientId: string }>()
  const navigate = useNavigate()
  const { data: patient, isLoading, error, refetch } = usePatientRecord(patientId ?? '')

  if (!patientId) {
    return <Navigate to="/patients" replace />
  }

  if (isLoading) {
    return <LoadingPage variant="detail" />
  }

  if (error || !patient) {
    return <ErrorState message="Failed to load patient record." onRetry={() => refetch()} />
  }

  const age = calculateAge(patient.date_of_birth)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/patients')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-semibold text-slate-900 truncate">
          {patient.first_name} {patient.last_name}
        </h1>
      </div>

      {/* Demographics */}
      <Card>
        <CardHeader>
          <CardTitle>Demographics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <InfoField label="Date of Birth" value={`${formatDate(patient.date_of_birth)} (${age})`} />
            <InfoField label="Sex at Birth" value={patient.medical_history ? formatSex(patient.medical_history.sex_at_birth) : '—'} />
            <InfoField label="Gender Identity" value={patient.medical_history ? formatGender(patient.medical_history.gender_identity) : '—'} />
            <InfoField label="Phone" value={patient.phone} />
            <InfoField label="Email" value={patient.email} />
            <InfoField label="Address" value={patient.address} />
          </div>
        </CardContent>
      </Card>

      {/* Medical History */}
      <MedicalHistoryPanel medicalHistory={patient.medical_history} />

      {/* Visit Timeline */}
      <div>
        <h2 className="mb-3 text-lg font-medium text-slate-900">Visit Timeline</h2>
        {patient.visits.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">No visits recorded</p>
        ) : (
          <div className="space-y-2">
            {patient.visits.map((visit) => (
              <VisitEntry key={visit.id} visit={visit} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <span className="text-sm text-slate-700">{value}</span>
    </div>
  )
}
